import { useState } from "react"
import type { CSSProperties, ReactNode } from "react"
import { Shield, BadgeCheck } from "lucide-react"
import type { SectionProps } from "../types"
import type { Organization } from "../types"
import { Button } from "@/components/ui/button"
import { SectionContainer } from "../components/SectionContainer"
import { DonationModal } from "@/components/DonationModal"
import { textFromRichHtml } from "@/components/blocks/render/shared"
import type { BlockStyleOverrides } from "@/components/website-builder/blocks/types"

function resolveElStyle(elStyles: Record<string, unknown> | undefined, key: string): CSSProperties {
  if (!elStyles) return {}
  const s = elStyles[key] as BlockStyleOverrides | undefined
  if (!s) return {}
  const result: CSSProperties = {}
  if (s.text_color) result.color = s.text_color
  if (s.text_align) result.textAlign = s.text_align
  return result
}

interface DonationCTASectionProps extends SectionProps {
  org?: Organization | null
}

export function DonationCTASection({ content, customizations, org, styleOverrides }: DonationCTASectionProps) {
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false)
  const headingDeleted = (content?.heading_deleted || content?.heading_hidden) as boolean | undefined
  const bodyDeleted = (content?.body_deleted || content?.body_hidden) as boolean | undefined
  const ctaDeleted = (content?.cta_text_deleted || content?.cta_text_hidden) as boolean | undefined
  const heading = (content?.heading as string) ?? "Support our mission"
  const body =
    textFromRichHtml(content?.body) ||
    "Your donation makes a difference. Every gift helps us continue our work in the community."
  const ctaText = (content?.cta_text as string) ?? "Donate now"
  // NOS-236: optional secondary CTA (e.g. Volunteer beside Donate)
  const secondaryDeleted = (content?.secondary_cta_text_deleted || content?.secondary_cta_text_hidden) as boolean | undefined
  const secondaryText = secondaryDeleted ? "" : (((content?.secondary_cta_text as string) ?? "").trim())
  const secondaryUrl = ((content?.secondary_cta_url as string) ?? "").trim() || "#"

  return (
    <SectionContainer
      id="donate"
      className={styleOverrides?.className ?? ""}
      style={styleOverrides?.style}
    >
      <div
        className="relative px-6 py-12 text-center @sm:px-10 @sm:py-16 @md:px-16 @md:py-20"
        style={{
          background: "linear-gradient(135deg, var(--nos-color-primary, #3b82f6) 0%, var(--nos-color-accent, var(--nos-color-secondary, #1e40af)) 100%)",
          borderRadius: "var(--nos-radius, 16px)",
          boxShadow: "var(--nos-elevation-shadow, 0 4px 16px rgb(0 0 0 / 0.12))",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: "rgba(0,0,0,0.15)",
            pointerEvents: "none",
            borderRadius: "var(--nos-radius, 16px)",
          }}
          aria-hidden
        />
        <div className="relative z-10">
        {(() => {
          const order = (content?.element_order as string[] | undefined) ?? ["heading", "body", "amounts", "cta_text"]
          const elStyles = content?._el_styles as Record<string, unknown> | undefined
          const map: Record<string, ReactNode> = {}
          if (!headingDeleted) map["heading"] = (
            <h2 key="heading" className="text-3xl font-bold tracking-tight text-white @sm:text-4xl @md:text-5xl" style={resolveElStyle(elStyles, "heading")}>
              {heading}
            </h2>
          )
          if (!bodyDeleted) map["body"] = (
            <p key="body" className="mx-auto mt-4 max-w-xl text-lg text-white/95 @sm:text-xl" style={resolveElStyle(elStyles, "body")}>
              {body}
            </p>
          )
          if (!ctaDeleted) {
            const donateButton = (
              <Button
                key="cta_text"
                size="lg"
                className={`${secondaryText ? "" : "mt-8 "}min-h-[48px] min-w-[200px] px-8 font-semibold shadow-lg transition-all hover:scale-105 hover:opacity-90 focus:ring-white/50 @sm:min-h-[52px] @sm:px-10 @sm:text-lg`}
                style={{
                  backgroundColor: "var(--nos-button-on-primary-bg, #ffffff)",
                  color: "var(--nos-button-on-primary-color, #111827)",
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor: "var(--nos-button-on-primary-border-color, transparent)",
                  borderRadius: "var(--nos-button-radius, 8px)",
                }}
                onClick={() => setIsDonationModalOpen(true)}
              >
                {ctaText}
              </Button>
            )
            // NOS-236: with a secondary CTA the pair renders side-by-side
            // (stacked on mobile); without one the markup is untouched.
            map["cta_text"] = secondaryText ? (
              <div key="cta_text" className="mt-8 flex flex-col items-center justify-center gap-3 @sm:flex-row">
                {donateButton}
                <Button
                  key="secondary_cta"
                  size="lg"
                  variant="outline"
                  className="min-h-[48px] min-w-[200px] px-8 font-semibold transition-all hover:scale-105 hover:bg-white/10 @sm:min-h-[52px] @sm:px-10 @sm:text-lg"
                  style={{
                    backgroundColor: "transparent",
                    color: "#ffffff",
                    borderWidth: 2,
                    borderStyle: "solid",
                    borderColor: "rgba(255,255,255,0.8)",
                    borderRadius: "var(--nos-button-radius, 8px)",
                  }}
                  asChild
                >
                  <a href={secondaryUrl}>{secondaryText}</a>
                </Button>
              </div>
            ) : donateButton
          }
          return order.map((k) => map[k] ?? null)
        })()}
        {org && (
          <DonationModal
            isOpen={isDonationModalOpen}
            onClose={() => setIsDonationModalOpen(false)}
            orgId={org.id}
            orgName={org.name}
            primaryColor={customizations?.primary_color} // hex required: feeds Stripe Elements appearance, which cannot resolve CSS vars
          />
        )}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-white/90">
          <span className="flex items-center gap-2">
            <BadgeCheck className="h-5 w-5" strokeWidth={2} />
            Tax-deductible
          </span>
          <span className="flex items-center gap-2">
            <Shield className="h-5 w-5" strokeWidth={2} />
            Secure donation
          </span>
        </div>
        </div>
      </div>
    </SectionContainer>
  )
}
